import type {IssuePickerItem, SuggestionSelection, SuggestionsShown} from '@github-ui/item-picker/IssuePicker'
import {type PickerType, RepositoryAndIssuePicker} from '@github-ui/item-picker/RepositoryAndIssuePicker'
import {getSemanticSearchType} from '@github-ui/item-picker-base/shared'
import {noop} from '@github-ui/noop'
import {useQuery} from '@github-ui/react-query'
import {createServerPersistedQueryPersister} from '@github-ui/react-query-persister'
import {DEFAULT_RELAY_CACHE_TTL} from '@github-ui/relay-query-cache/helpers'
import {DependenciesPickerRelationshipsServerQuery} from '@github-ui/server-persisted-queries'
import {useAnalytics} from '@github-ui/use-analytics'
import {useCallback, useMemo, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {LABELS} from '../../../constants/labels'
import type {OptimisticDependencyIssue} from '../../../mutations/add-blocked-by-mutation'
import {useAddBlockedByMutation} from '../../../mutations/add-blocked-by-mutation'
import {useAddRelatesToMutation} from '../../../mutations/add-relates-to-mutation'
import {useRemoveBlockedByMutation} from '../../../mutations/remove-blocked-by-mutation'
import {useRemoveRelatesToMutation} from '../../../mutations/remove-relates-to-mutation'
import styles from './DependenciesPicker.module.css'
import type {DependenciesPickerRelationshipsIssue} from './fetch-dependencies-picker-relationships'
import {
  dependenciesPickerRelationshipsQueryKey,
  fetchDependenciesPickerRelationships,
} from './fetch-dependencies-picker-relationships'

export type DependenciesPickerProps = {
  type: 'blockedBy' | 'blocking' | 'relatesTo'
  onClose?: () => void
  issueId: string
  issueNumber: number
  issueTitle: string
  organization: string
  defaultRepositoryNameWithOwner: string
  anchorRef: React.RefObject<HTMLElement | null>
  onError?: (errors: Error[]) => void
  onSelection: () => void
}

function diffSelection(selectedIssues: IssuePickerItem[], currentIds: string[]) {
  const selectedIds = new Set(selectedIssues.map(issue => issue.id))
  const currentIdSet = new Set(currentIds)
  return {
    issuesToAdd: [...selectedIds].filter(id => !currentIdSet.has(id)),
    issuesToRemove: [...currentIdSet].filter(id => !selectedIds.has(id)),
  }
}

function toOptimistic(item: IssuePickerItem): OptimisticDependencyIssue {
  return {
    id: item.id,
    title: item.title,
    url: item.url,
    number: item.number,
    state: item.state,
    stateReason: item.stateReason,
    repository: {
      id: item.repository.id,
      nameWithOwner: item.repository.nameWithOwner,
    },
  }
}

type DependenciesPickerDataProps = DependenciesPickerProps & {
  queryData: {node: DependenciesPickerRelationshipsIssue | null} | null
  state: 'loading' | 'error' | 'success' | 'idle'
}

export function DependenciesPicker({
  type,
  onClose,
  issueId,
  issueNumber,
  issueTitle,
  organization,
  defaultRepositoryNameWithOwner,
  anchorRef,
  onError = noop,
  onSelection,
  queryData,
  state,
}: DependenciesPickerDataProps) {
  const environment = useRelayEnvironment()
  const {sendAnalyticsEvent} = useAnalytics()
  const addRelatesTo = useAddRelatesToMutation({
    issueId,
    issueNumber,
    nameWithOwner: defaultRepositoryNameWithOwner,
  })
  const removeRelatesTo = useRemoveRelatesToMutation({
    issueId,
    issueNumber,
    nameWithOwner: defaultRepositoryNameWithOwner,
  })
  const addBlockedBy = useAddBlockedByMutation({
    issueId,
    issueNumber,
    nameWithOwner: defaultRepositoryNameWithOwner,
  })
  const removeBlockedBy = useRemoveBlockedByMutation({
    issueId,
    issueNumber,
    nameWithOwner: defaultRepositoryNameWithOwner,
  })

  // Only the "Relates to" picker suggests similar issues; blocked-by/blocking share this component.
  const semanticQuery = type === 'relatesTo' ? issueTitle : undefined

  // Semantic search is unavailable on GHES and ineligible repos, so track whether it actually ran.
  const semanticAvailable = Boolean(semanticQuery) && getSemanticSearchType(environment) !== undefined

  const [pickerType, setPickerType] = useState<PickerType>('Issue')

  const blockedByNodes = queryData?.node?.blockedBy?.nodes
  const blockingNodes = queryData?.node?.blocking?.nodes
  const relatesToNodes = queryData?.node?.relatesTo?.nodes

  const blockedByIssueIds = useMemo(() => {
    if (!blockedByNodes) return []
    return blockedByNodes.filter(issue => !!issue).map(issue => issue.id)
  }, [blockedByNodes])

  const blockingIssueIds = useMemo(() => {
    if (!blockingNodes) return []
    return blockingNodes.filter(issue => !!issue).map(issue => issue.id)
  }, [blockingNodes])

  const relatesToIssueIds = useMemo(() => {
    if (!relatesToNodes) return []
    return relatesToNodes.filter(issue => !!issue).map(issue => issue.id)
  }, [relatesToNodes])

  const onPickerTypeChange = useCallback(
    (t: PickerType) => {
      setPickerType(t)
      if (t === null && onClose) {
        onClose()
      }
    },
    [onClose],
  )

  const commitBlockedBy = useCallback(
    async (selectedIssues: IssuePickerItem[]) => {
      const issueById = new Map(selectedIssues.map(issue => [issue.id, issue]))
      const {issuesToAdd, issuesToRemove} = diffSelection(selectedIssues, blockedByIssueIds)

      sendAnalyticsEvent(`issue_viewer.dependencies_picker.blocked_by`, 'ISSUE_DEPENDENCIES_PICKER', {
        issuesToAdd: issuesToAdd.length,
        issuesToRemove: issuesToRemove.length,
      })

      const errors: Error[] = []
      for (const blockingIssueId of issuesToRemove) {
        try {
          await removeBlockedBy(blockingIssueId, 'blockedBy')
        } catch (error) {
          errors.push(error instanceof Error ? error : new Error('Failed to remove dependency'))
        }
      }
      for (const blockingIssueId of issuesToAdd) {
        const issue = issueById.get(blockingIssueId)
        if (!issue) {
          errors.push(new Error('Failed to add dependency'))
          continue
        }
        try {
          await addBlockedBy(toOptimistic(issue), 'blockedBy')
        } catch (error) {
          errors.push(error instanceof Error ? error : new Error('Failed to add dependency'))
        }
      }

      onError(errors)
    },
    [blockedByIssueIds, sendAnalyticsEvent, addBlockedBy, removeBlockedBy, onError],
  )

  const commitBlocking = useCallback(
    async (selectedIssues: IssuePickerItem[]) => {
      const issueById = new Map(selectedIssues.map(issue => [issue.id, issue]))
      const {issuesToAdd, issuesToRemove} = diffSelection(selectedIssues, blockingIssueIds)

      sendAnalyticsEvent(`issue_viewer.dependencies_picker.blocking`, 'ISSUE_DEPENDENCIES_PICKER', {
        issuesToAdd: issuesToAdd.length,
        issuesToRemove: issuesToRemove.length,
      })

      // 'blocking' inverts the relationship: the selected issue is blocked by the current issue.
      const errors: Error[] = []
      for (const blockedIssueId of issuesToRemove) {
        try {
          await removeBlockedBy(blockedIssueId, 'blocking')
        } catch (error) {
          errors.push(error instanceof Error ? error : new Error('Failed to remove dependency'))
        }
      }
      for (const blockedIssueId of issuesToAdd) {
        const issue = issueById.get(blockedIssueId)
        if (!issue) {
          errors.push(new Error('Failed to add dependency'))
          continue
        }
        try {
          await addBlockedBy(toOptimistic(issue), 'blocking')
        } catch (error) {
          errors.push(error instanceof Error ? error : new Error('Failed to add dependency'))
        }
      }

      onError(errors)
    },
    [blockingIssueIds, sendAnalyticsEvent, addBlockedBy, removeBlockedBy, onError],
  )

  const commitRelatesTo = useCallback(
    async (selectedIssues: IssuePickerItem[]) => {
      const issueById = new Map(selectedIssues.map(issue => [issue.id, issue]))
      const {issuesToAdd, issuesToRemove} = diffSelection(selectedIssues, relatesToIssueIds)

      sendAnalyticsEvent(`issue_viewer.dependencies_picker.relates_to`, 'ISSUE_DEPENDENCIES_PICKER', {
        issuesToAdd: issuesToAdd.length,
        issuesToRemove: issuesToRemove.length,
        semanticAvailable,
      })

      const errors: Error[] = []
      for (const relatedIssueId of issuesToRemove) {
        try {
          await removeRelatesTo(relatedIssueId)
        } catch (error) {
          errors.push(error instanceof Error ? error : new Error('Failed to remove related issue'))
        }
      }
      for (const relatedIssueId of issuesToAdd) {
        const relatedIssue = issueById.get(relatedIssueId)
        if (!relatedIssue) {
          errors.push(new Error('Selected related issue was missing'))
          continue
        }
        try {
          await addRelatesTo(toOptimistic(relatedIssue))
        } catch (error) {
          errors.push(error instanceof Error ? error : new Error('Failed to add related issue'))
        }
      }

      onError(errors)
    },
    [relatesToIssueIds, sendAnalyticsEvent, semanticAvailable, addRelatesTo, removeRelatesTo, onError],
  )

  const onIssueSelection = useCallback(
    async (selectedIssues: IssuePickerItem[]) => {
      if (type === 'blockedBy') {
        await commitBlockedBy(selectedIssues)
      } else if (type === 'blocking') {
        await commitBlocking(selectedIssues)
      } else if (type === 'relatesTo') {
        await commitRelatesTo(selectedIssues)
      }
      onSelection()
    },
    [type, commitBlockedBy, commitBlocking, commitRelatesTo, onSelection],
  )

  // Basic engagement telemetry for the semantic suggestions experiment (github/issues#23315): which
  // suggestion the viewer picked, whether it came from semantic or lexical search, its rank, and
  // whether they picked before typing.
  const onSuggestionSelected = useCallback(
    (selection: SuggestionSelection) => {
      sendAnalyticsEvent(
        'issue_viewer.dependencies_picker.relates_to.suggestion_selected',
        'ISSUE_DEPENDENCIES_PICKER',
        {
          ...selection,
          semanticAvailable,
        },
      )
    },
    [sendAnalyticsEvent, semanticAvailable],
  )

  const onSearchTermEntered = useCallback(() => {
    sendAnalyticsEvent('issue_viewer.dependencies_picker.relates_to.search_typed', 'ISSUE_DEPENDENCIES_PICKER', {
      semanticAvailable,
    })
  }, [sendAnalyticsEvent, semanticAvailable])

  // The impression event: fires once when the initial suggestion list is shown, giving the
  // denominator for "what fraction of sessions engaged with the suggestions?".
  const onSuggestionsShown = useCallback(
    (metadata: SuggestionsShown) => {
      sendAnalyticsEvent('issue_viewer.dependencies_picker.relates_to.suggestions_shown', 'ISSUE_DEPENDENCIES_PICKER', {
        ...metadata,
        semanticAvailable,
      })
    },
    [sendAnalyticsEvent, semanticAvailable],
  )

  const hiddenIssueIds = useMemo(() => {
    const ids = [issueId]
    if (type === 'blockedBy') {
      ids.push(...blockingIssueIds)
    } else if (type === 'blocking') {
      ids.push(...blockedByIssueIds)
    }
    return ids
  }, [type, issueId, blockingIssueIds, blockedByIssueIds])

  const selectedIssueIds =
    type === 'blockedBy' ? blockedByIssueIds : type === 'blocking' ? blockingIssueIds : relatesToIssueIds

  const subtitle =
    type === 'blockedBy'
      ? 'Mark current issue as blocked by…'
      : type === 'blocking'
        ? 'Mark current issue as blocking…'
        : LABELS.relatesToPickerSubtitle

  return (
    <RepositoryAndIssuePicker
      anchorElement={props => {
        const {ref} = props as {
          ref: React.RefObject<HTMLElement | null>
        }
        if (ref) {
          ref.current = anchorRef.current
        }
        return <></>
      }}
      onIssueSelection={onIssueSelection}
      organization={organization}
      defaultRepositoryNameWithOwner={defaultRepositoryNameWithOwner}
      pickerType={pickerType}
      onPickerTypeChange={onPickerTypeChange}
      selectedIssueIds={selectedIssueIds}
      hiddenIssueIds={hiddenIssueIds}
      issuePickerProps={{
        selectionVariant: 'multiple',
        isLoading: state === 'loading',
        subtitle,
        // "relates to" is symmetric and needs triage on the target, so only offer candidates the
        // viewer can relate to (blocking/blocked-by need only read access).
        requireTargetTriage: type === 'relatesTo',
        className: styles.DependenciesPicker,
        semanticQuery,
        // Only the "relates to" picker surfaces suggestions worth measuring engagement on.
        ...(type === 'relatesTo' ? {onSuggestionSelected, onSearchTermEntered, onSuggestionsShown} : {}),
        notice:
          state === 'error'
            ? {
                variant: 'error',
                text: `We couldn't load the issues. Try reloading the page, or if the problem persists, contact support.`,
              }
            : undefined,
      }}
    />
  )
}

export function DependenciesPickerLoader(props: DependenciesPickerProps) {
  const {defaultRepositoryNameWithOwner, issueNumber} = props
  const {sendAnalyticsEvent} = useAnalytics()

  const persister = useMemo(
    () =>
      createServerPersistedQueryPersister({
        queryHash: DependenciesPickerRelationshipsServerQuery,
        sendAnalyticsEvent,
        maxAgeMs: DEFAULT_RELAY_CACHE_TTL,
        // Restored data may be stale within its TTL, so revalidate after restoring it.
        alwaysRefetch: true,
      }),
    [sendAnalyticsEvent],
  )

  const query = useQuery({
    queryKey: dependenciesPickerRelationshipsQueryKey(defaultRepositoryNameWithOwner, issueNumber),
    queryFn: ({signal}) => fetchDependenciesPickerRelationships(defaultRepositoryNameWithOwner, issueNumber, signal),
    gcTime: DEFAULT_RELAY_CACHE_TTL,
    persister,
  })

  return (
    <DependenciesPicker
      {...props}
      queryData={query.data ? {node: query.data} : null}
      // isPending rather than isFetching: once restored data is available it should be shown, even
      // while the background revalidation is still in flight. Likewise a failed revalidation keeps
      // the restored data, so only surface the error when there is nothing usable to show.
      state={query.isPending ? 'loading' : query.isError && !query.data ? 'error' : 'success'}
    />
  )
}
